import {
  FileBarChart,
  GitBranch,
  LayoutTemplate,
  Sparkles,
} from 'lucide-react'
import { Logo } from '../Logo'
import { PreLoginSupportCallout } from '../PreLoginSupportCallout'
import { env, buildLabel } from '../../lib/env'

/**
 * Split-screen layout for Sign In / Sign Up / Forgot Password.
 * Navy brand panel on the left, off-white canvas on the right.
 */
/**
 * Split-screen layout for Sign In / Sign Up / Forgot Password.
 *
 *   - `wide` — loosens the form-card max-width so the Sign-up plan
 *     picker can fit a 3-column card grid. Other auth pages stay at
 *     the default narrow column.
 *   - `centered` — opt-in layout for Sign In: floating logo above the
 *     form card, centered "Welcome back" / "Sign in to continue…"
 *     hero inside the card, and the page's own form chrome (OR
 *     divider, Google CTA, signup link) rendered as children. When
 *     `centered` is true the layout ignores `title` / `subtitle` /
 *     `footer` props — the child owns the entire card body.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  wide = false,
  centered = false,
}) {
  return (
    <div className="theme-light taskmagix-page-bg min-h-screen lg:flex">
      {/* Brand panel — Deep Navy → Ocean gradient (matches the
       *  sidebar's chrome). Text + tokens are scoped to white so
       *  the heading-accent, feature cards, and copy all render
       *  legibly on the dark surface. */}
      <aside
        className="relative hidden lg:flex lg:grow-[45] lg:basis-0 lg:min-w-0 flex-col p-12 lg:pl-16 xl:pl-20 overflow-hidden text-white"
        style={{
          // Multi-stop gradient mesh — darker navy at the edges, ocean
          // and electric-cyan glow concentrated through the middle
          // band. The radial-gradient layers stack on top of a base
          // linear gradient so each glow lights a different region
          // (top-right, mid-left, bottom-centre).
          background: `
            radial-gradient(ellipse 60% 50% at 80% 20%, rgba(4, 186, 222, 0.22), transparent 70%),
            radial-gradient(ellipse 55% 55% at 15% 40%, rgba(4, 150, 199, 0.28), transparent 70%),
            radial-gradient(ellipse 50% 50% at 65% 75%, rgba(0, 108, 165, 0.30), transparent 70%),
            linear-gradient(160deg, #01225A 0%, #02367B 35%, #034A8E 65%, #02367B 100%)
          `,
          // Scoped CSS-variable overrides so the existing tokens used
          // by `heading-accent`, `FeatureCard`, and the muted copy
          // resolve to white-on-dark instead of dark-on-light.
          '--color-text': '#ffffff',
          '--color-muted': 'rgba(255, 255, 255, 0.78)',
          '--color-subtle': 'rgba(255, 255, 255, 0.55)',
          '--color-accent': '#04BADE',
          '--color-accent-bg': 'rgba(255, 255, 255, 0.10)',
          '--color-accent-border': 'rgba(255, 255, 255, 0.20)',
          '--color-border': 'rgba(255, 255, 255, 0.15)',
          '--color-surface': 'rgba(255, 255, 255, 0.06)',
        }}
      >
        {/* Logo + content grouped together so the parent's
         *  `justify-between` only stretches the gap BELOW the
         *  feature cards (toward the copyright line), not BETWEEN
         *  the logo and the heading. Earlier these were sibling
         *  children of the aside, which made `justify-between`
         *  distribute the whole vertical viewport — visually
         *  parking the heading in the middle of the panel with a
         *  big empty stripe below the logo. */}
        <div className="relative z-10">
          <div className="mb-10">
            <Logo size="lg" />
          </div>

          <div className="max-w-2xl mt-6">
            <h2 className="font-display text-[33px] leading-[1.15] font-semibold tracking-tight whitespace-nowrap">
            Built For Teams That Run On{' '}
            <span className="text-[color:var(--color-accent)]">SOPs.</span>
          </h2>
          <p className="mt-5 text-justify text-[color:var(--color-muted)] max-w-xl">
            TaskMagix connects your SOP library to every task — automates
            workflows, tracks execution, and delivers real-time insights.
            All in one intelligent workspace.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            <FeatureCard
              icon={<Sparkles size={18} />}
              title="AI Task Generation"
              body="Turn any SOP or brief into a ready-to-run task tree. Our AI drafts ordered subtasks you review and approve inline."
            />
            <FeatureCard
              icon={<LayoutTemplate size={18} />}
              title="Reusable Blueprints"
              body="Every AI breakdown is saved as a Blueprint, so repeat work rebuilds instantly at no extra cost."
            />
            <FeatureCard
              icon={<GitBranch size={18} />}
              title="Departments and Workflows"
              body="Give each department its own workflow and statuses, so every team tracks work the way it actually runs."
            />
            <FeatureCard
              icon={<FileBarChart size={18} />}
              title="AI-Drafted Work Reports"
              body="Daily, Weekly, and Monthly reports with in-app reminders and AI-drafted summaries."
            />
          </div>

          </div>
        </div>

        <p className="mt-8 text-sm text-[color:var(--color-subtle)] relative z-10">
          © {new Date().getFullYear()}{' '}
          <a
            href={env.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white transition-colors underline-offset-2 hover:underline"
          >
            TaskMagix.com
          </a>
          . All rights reserved.
        </p>
        {/* Build stamp - visible without signing in, so a tester can confirm
         *  which build they are looking at (QA-017). Deliberately quiet. */}
        <p className="mt-1 text-xs text-[color:var(--color-subtle)] relative z-10 font-mono">
          build {buildLabel()}
        </p>

        {/* ─────────── Decoration stack ───────────────────────────
         *  Layered SVG + blur orbs for cinematic depth. Order top to
         *  bottom (back → front):
         *    1. Vignette darken at corners
         *    2. Wave / curve paths (network of flowing lines)
         *    3. Dot constellation (sparse white particles)
         *    4. Light streak diagonal
         *    5. Blur orbs (cyan halos) positioned around headline +
         *       feature grid
         *    6. Radial glow centred behind the headline
         *  Content sits above the whole stack via `relative z-10`. */}

        {/* 1. Vignette — darker navy edges so the centre pops. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 40%, rgba(1, 17, 51, 0.55) 100%)',
          }}
        />

        {/* 2. Curve / wave network — multiple opacity-layered paths
         *  drifting diagonally so the panel feels like a flowing
         *  surface rather than a flat colour. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 800 1000"
          preserveAspectRatio="xMaxYMid slice"
          className="pointer-events-none absolute inset-0 w-full h-full"
        >
          <defs>
            <linearGradient id="auth-wave-cyan" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#04BADE" stopOpacity="0.30" />
              <stop offset="100%" stopColor="#04BADE" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="auth-wave-white" x1="0" y1="0" x2="1" y2="0.5">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Primary curves — flowing diagonally top-right → bottom-left. */}
          <path
            d="M820 60 C 620 140, 540 260, 720 400 S 820 700, 540 860"
            stroke="url(#auth-wave-cyan)"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M860 140 C 660 220, 580 340, 760 480 S 860 780, 580 940"
            stroke="url(#auth-wave-cyan)"
            strokeWidth="1"
            fill="none"
            opacity="0.7"
          />
          <path
            d="M900 220 C 700 300, 620 420, 800 560 S 900 860, 620 1020"
            stroke="url(#auth-wave-cyan)"
            strokeWidth="0.8"
            fill="none"
            opacity="0.5"
          />

          {/* Secondary curves — counter-flow from bottom-right up to
           *  top-left, adds a network-feel without crowding. */}
          <path
            d="M-40 920 C 180 820, 260 700, 80 560 S -40 240, 240 80"
            stroke="url(#auth-wave-white)"
            strokeWidth="0.8"
            fill="none"
            opacity="0.6"
          />
          <path
            d="M-80 840 C 140 740, 220 620, 40 480 S -80 160, 200 0"
            stroke="url(#auth-wave-white)"
            strokeWidth="0.6"
            fill="none"
            opacity="0.4"
          />

          {/* Diagonal light streak — thin bright line that catches the
           *  eye, suggests motion/depth (no animation, just a static
           *  cinematic accent). */}
          <line
            x1="-50"
            y1="200"
            x2="850"
            y2="650"
            stroke="#ffffff"
            strokeOpacity="0.10"
            strokeWidth="1"
          />
          <line
            x1="-50"
            y1="220"
            x2="850"
            y2="670"
            stroke="#04BADE"
            strokeOpacity="0.18"
            strokeWidth="0.6"
          />
        </svg>

        {/* 5a. Cyan halo behind the headline area — soft radial glow
         *  that pulls focus to the "Built for teams" headline. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-[28%] left-[18%] h-96 w-96 rounded-full opacity-30 blur-[120px]"
          style={{ background: '#04BADE' }}
        />

        {/* 5b. Secondary halo behind the feature grid — deeper ocean
         *  tone so the cards sit on a lit surface. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[18%] left-[8%] h-80 w-80 rounded-full opacity-25 blur-[140px]"
          style={{ background: '#0496C7' }}
        />

        {/* 5c. Top-right corner glow — adds depth into the corner
         *  where the wave paths originate. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-28 -right-28 h-96 w-96 rounded-full opacity-20 blur-[100px]"
          style={{ background: '#04BADE' }}
        />

        {/* 5d. Bottom-left ambient — fades into the corner so the
         *  copyright row doesn't sit on flat colour. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full opacity-12 blur-[140px]"
          style={{ background: '#0496C7' }}
        />
      </aside>

      {/* Form panel — solid pale-cyan canvas (matches the brand
       *  page background). The card sits on top with a strong-but-
       *  soft shadow and tight typography. */}
      <main
        className="relative flex lg:grow-[55] lg:basis-0 lg:min-w-0 items-center justify-center min-h-screen p-6 py-12 lg:min-h-0 lg:p-12 text-[color:var(--color-ink)] overflow-clip"
        style={{ background: '#f0f8fc' }}
      >
        {/* Decorative dot grid in the top-right corner — pale white
         *  dots scattered over the pale-cyan canvas. SVG so the
         *  pattern stays crisp on every display. */}
        <svg
          aria-hidden="true"
          width="180"
          height="180"
          className="pointer-events-none absolute top-8 right-8 opacity-50"
        >
          <defs>
            <pattern
              id="auth-dot-pattern"
              x="0"
              y="0"
              width="14"
              height="14"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="2" cy="2" r="1.4" fill="#ffffff" />
            </pattern>
          </defs>
          <rect width="180" height="180" fill="url(#auth-dot-pattern)" />
        </svg>
        {/* Concentric ring motif in the bottom-right — soft white
         *  outlines that suggest depth without competing with the
         *  form card. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full border-2 border-white/60"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-12 -right-12 h-48 w-48 rounded-full border-2 border-white/70"
        />
        {/* Soft circular orb bottom-left for balance. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full border-2 border-white/50"
        />
        <div
          className={
            'w-full relative z-10 ' +
            (wide ? 'max-w-[1080px]' : 'max-w-[440px]')
          }
        >
          {/* Mobile-only brand: show the logo on a navy bar up top
           *  (only when NOT using the centered layout — centered has
           *  its own floating logo). */}
          {!centered ? (
            <div className="lg:hidden flex justify-center mb-8">
              <div
                className="py-4 px-6 rounded-lg"
                style={{ background: '#02367B' }}
              >
                <Logo size="md" />
              </div>
            </div>
          ) : null}

          {/* Form card — flat white with a strong-but-soft shadow.
           *  When `centered`, the card has extra top padding to make
           *  room for the floating logo (positioned absolutely above
           *  the card's top edge) and the welcome heading is
           *  centered. */}
          <div
            className={
              'relative rounded-3xl bg-[color:var(--color-card)] shadow-[0_24px_60px_-30px_rgba(15,23,42,0.28),0_8px_24px_-12px_rgba(15,23,42,0.10)] ' +
              (centered ? 'pt-20 pb-10 px-8 lg:px-10' : 'p-10 lg:p-12')
            }
          >
            {/* Floating circular logo — half-overlapping the card's
             *  top edge. Anchored to the card via `absolute -top-10`
             *  + `left-1/2 -translate-x-1/2` so it's reliably
             *  centered regardless of card width. Background is the
             *  brand Deep Navy so the white `/login.png` mark inside
             *  reads with strong contrast. */}
            {centered ? (
              <div
                className="absolute -top-10 left-1/2 -translate-x-1/2 h-20 w-20 rounded-full border border-white/20 shadow-[0_8px_20px_-6px_rgba(2,54,123,0.35)] flex items-center justify-center z-10 overflow-hidden"
                style={{
                  background:
                    'linear-gradient(135deg, #02367B 0%, #006CA5 100%)',
                }}
              >
                <img
                  src="/login.png"
                  alt="TaskMagix"
                  className="h-12 w-12 object-contain"
                />
              </div>
            ) : null}
            {centered ? (
              <div className="mb-8 text-center">
                {/* opts out of the global h1 `text-transform:
                 *  capitalize` rule (index.css). Without it, CSS upper-
                 *  cases EVERY word in the title — including small ones
                 *  like "a", "the", "for" — producing "Set A New
                 *  Password" instead of proper English title case ("Set
                 *  a New Password"). Source strings are now expected
                 *  to be authored in the exact case they should render. */}
                <h1 className="font-display text-[28px] leading-tight text-[color:var(--color-ink-strong)] tracking-tight font-semibold">
                  {title}
                </h1>
                {subtitle ? (
                  <p className="mt-2 text-sm text-[color:var(--color-ink-muted)]">
                    {subtitle}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="mb-8">
                <h1 className="font-display text-[26px] leading-tight text-[color:var(--color-ink-strong)] tracking-tight">
                  {title}
                </h1>
                {subtitle ? (
                  <p className="mt-2 text-sm text-[color:var(--color-ink-muted)]">
                    {subtitle}
                  </p>
                ) : null}
              </div>
            )}

            {children}
          </div>

          {footer ? (
            <div className="mt-6 text-center text-[15px] text-[color:var(--color-ink-muted)]">
              {footer}
            </div>
          ) : null}

          {/* [Servexa] Pre-login "Report an Issue" (spec A6a) — a prominent help
           *  callout for users who cannot authenticate. Renders itself only when
           *  the backend confirms the flow is configured, and posts through our
           *  own relay so no secret ever reaches the browser. */}
          <PreLoginSupportCallout />
        </div>
      </main>
    </div>
  )
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-4 backdrop-blur-sm">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--color-accent)]/15 text-[color:var(--color-accent)] border border-[color:var(--color-accent)]/30">
        {icon}
      </span>
      <p className="mt-3 text-[15px] font-semibold text-white">{title}</p>
      <p className="mt-1.5 text-justify text-[14px] text-[color:var(--color-muted)] leading-relaxed">
        {body}
      </p>
    </div>
  )
}
